/**
 * TicketsPanel — unified inbox-overview for admin dashboard (iter 47).
 *
 * Lists all open tickets from contact/business-lead/AI-quote/driver-app/vognmand-flows
 * with SLA timer, source filter, inbox filter, and inline reply.
 */
import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Mail, Clock, AlertTriangle, CheckCircle2, MessageSquare, Send, X, User, Phone, ArrowUpRight, Sparkles, Loader2, Keyboard } from "lucide-react";

const SOURCE_LABELS = {
  contact: { label: "Kontakt", color: "#0EA5E9" },
  business_lead: { label: "B2B-lead", color: "#DC2626" },
  b2b_quote: { label: "AI-tilbud", color: "#7C3AED" },
  driver_application: { label: "Chauffør-ansøgning", color: "#F59E0B" },
  subcontractor: { label: "Vognmand", color: "#10B981" },
  customer_service: { label: "Kundeservice", color: "#EC4899" },
  manual: { label: "Manuel", color: "#64748B" },
};

const STATUS_LABELS = {
  open: { label: "Ny", color: "#DC2626" },
  in_progress: { label: "Under behandling", color: "#F59E0B" },
  waiting_customer: { label: "Afventer kunde", color: "#7C3AED" },
  closed: { label: "Løst", color: "#10B981" },
  archived: { label: "Arkiveret", color: "#64748B" },
};

const PRIORITY_LABELS = {
  low: "Lav",
  normal: "Normal",
  high: "Høj",
  urgent: "Haster",
};

function timeLeft(iso) {
  if (!iso) return { label: "—", overdue: false };
  const due = new Date(iso).getTime();
  const diff = due - Date.now();
  const overdue = diff < 0;
  const abs = Math.abs(diff);
  const hours = Math.floor(abs / 3600000);
  const mins = Math.floor((abs % 3600000) / 60000);
  const label = hours > 24 ? `${Math.floor(hours / 24)}d ${hours % 24}t` : (hours >= 1 ? `${hours}t ${mins}m` : `${mins}m`);
  return { label: overdue ? `−${label}` : label, overdue };
}

function Pill({ children, color, bg }) {
  return (
    <span className="inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-sm"
      style={{ color, background: bg || `${color}15` }}>
      {children}
    </span>
  );
}

export default function TicketsPanel() {
  const [tickets, setTickets] = React.useState([]);
  const [stats, setStats] = React.useState({ open: 0, overdue: 0, mine: 0, current_admin: "", by_inbox: [] });
  const [loading, setLoading] = React.useState(true);
  const [filterSource, setFilterSource] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [filterInbox, setFilterInbox] = React.useState("");
  const [mineOnly, setMineOnly] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [highlightIdx, setHighlightIdx] = React.useState(-1);
  const [showHelp, setShowHelp] = React.useState(false);
  const ticketsRef = React.useRef([]);
  ticketsRef.current = tickets;

  const load = React.useCallback(async () => {
    try {
      const params = [];
      if (filterSource) params.push(`source=${encodeURIComponent(filterSource)}`);
      if (filterStatus) params.push(`status=${encodeURIComponent(filterStatus)}`);
      if (filterInbox) params.push(`inbox=${encodeURIComponent(filterInbox)}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/admin/tickets${qs}`),
        api.get("/admin/tickets/stats"),
      ]);
      // Client-side "Mine"-filter so we keep stats panel showing global counts
      const filtered = mineOnly && s?.current_admin
        ? list.filter((t) => t.assigned_to === s.current_admin)
        : list;
      setTickets(filtered);
      setStats(s);
    } catch (e) {
      console.warn("tickets load failed", e);
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterStatus, filterInbox, mineOnly]);

  React.useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Keyboard shortcuts (Gmail/Linear/Superhuman style)
  const lastGRef = React.useRef(0);
  React.useEffect(() => {
    const isFormElement = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };
    const onKey = (e) => {
      // ? opens help overlay even from input fields? Skip — only when not typing
      if (isFormElement(document.activeElement)) return;
      // Skip when detail modal is open (modal owns its own shortcuts)
      if (selected) return;
      const k = e.key;
      // g a / g m two-key sequences
      if (k === "g") {
        lastGRef.current = Date.now();
        return;
      }
      const gActive = Date.now() - lastGRef.current < 1200;
      if (gActive && k === "a") { e.preventDefault(); setMineOnly(false); lastGRef.current = 0; return; }
      if (gActive && k === "m") { e.preventDefault(); setMineOnly(true); lastGRef.current = 0; return; }
      // Single keys
      const list = ticketsRef.current;
      if (k === "?" || (e.shiftKey && k === "?")) { e.preventDefault(); setShowHelp((v) => !v); return; }
      if (k === "j") {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(list.length - 1, i + 1));
        return;
      }
      if (k === "k") {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (k === "Enter") {
        e.preventDefault();
        const idx = highlightIdx >= 0 ? highlightIdx : 0;
        if (list[idx]) setSelected(list[idx]);
        return;
      }
      if (k === "/") { e.preventDefault(); document.querySelector('[data-testid="tickets-filter-status"]')?.focus(); return; }
      if (k === "r" && !e.metaKey && !e.ctrlKey) { e.preventDefault(); load(); return; }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected, highlightIdx, load]);

  // Auto-scroll highlighted row into view
  React.useEffect(() => {
    if (highlightIdx < 0) return;
    const el = document.querySelector(`[data-ticket-idx="${highlightIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  return (
    <div data-testid="tickets-panel">
      {/* Top stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-slate-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2">Åbne tickets</div>
          <div className="font-display font-black text-3xl text-brand-dark mt-1">{stats.open}</div>
        </div>
        <div className={`p-4 border ${stats.overdue > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 flex items-center gap-1">
            {stats.overdue > 0 && <AlertTriangle size={11} className="text-red-600" />} SLA overskredet
          </div>
          <div className={`font-display font-black text-3xl mt-1 ${stats.overdue > 0 ? "text-red-700" : "text-brand-dark"}`}>{stats.overdue}</div>
        </div>
        {stats.by_inbox.slice(0, 2).map((b) => (
          <div key={b.inbox} className="bg-white border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 truncate">{b.inbox}</div>
            <div className="font-display font-black text-3xl text-brand-dark mt-1">{b.count}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      {mineOnly && (
        <div className="bg-brand-red/10 border-l-2 border-brand-red px-4 py-2 mb-4 text-xs text-brand-dark flex items-center justify-between" data-testid="tickets-mine-banner">
          <span><strong>Mine tickets-filter aktivt</strong> — viser kun tickets tildelt {stats.current_admin}</span>
          <button onClick={() => setMineOnly(false)} className="text-xs font-semibold text-brand-red hover:underline">Vis alle</button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <select className="input-flat py-1.5 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="tickets-filter-status">
          <option value="">Alle status</option>
          <option value="open">Nye</option>
          <option value="in_progress">Under behandling</option>
          <option value="waiting_customer">Afventer kunde</option>
          <option value="closed">Løste</option>
          <option value="archived">Arkiverede</option>
        </select>
        <select className="input-flat py-1.5 text-sm" value={filterSource} onChange={(e) => setFilterSource(e.target.value)} data-testid="tickets-filter-source">
          <option value="">Alle kilder</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="input-flat py-1.5 text-sm" value={filterInbox} onChange={(e) => setFilterInbox(e.target.value)} data-testid="tickets-filter-inbox">
          <option value="">Alle inboxes</option>
          {stats.by_inbox.map((b) => <option key={b.inbox} value={b.inbox}>{b.inbox} ({b.count})</option>)}
        </select>
        <button onClick={() => setShowHelp(true)} className="text-xs font-semibold text-purple-700 hover:text-purple-900 inline-flex items-center gap-1 ml-auto px-2 py-1 border border-purple-200 rounded-sm" data-testid="tickets-help-btn" title="Tastatur-genveje (?)">
          <Keyboard size={12} /> ? Genveje
        </button>
        <button onClick={load} className="text-sm font-semibold text-brand-red hover:underline">Genopfrisk</button>
      </div>

      {/* Tickets list */}
      {loading ? (
        <div className="text-brand-text2 py-12 text-center">Indlæser...</div>
      ) : tickets.length === 0 ? (
        <div className="text-brand-text2 py-12 text-center bg-white border border-slate-200">
          <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-3" />
          <div className="font-display font-bold text-xl text-brand-dark">Ingen tickets matcher filteret 🎉</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100">
          {tickets.map((t, idx) => {
            const src = SOURCE_LABELS[t.source] || SOURCE_LABELS.manual;
            const st = STATUS_LABELS[t.status] || STATUS_LABELS.open;
            const tl = timeLeft(t.sla_due_at);
            const isOpen = t.status !== "closed";
            const isHighlighted = idx === highlightIdx;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelected(t)}
                data-testid={`ticket-row-${t.id}`}
                data-ticket-idx={idx}
                className={`w-full text-left px-4 py-3 transition-colors flex items-center gap-4 ${
                  isHighlighted ? "bg-purple-50 border-l-2 border-l-purple-500" : "hover:bg-slate-50 border-l-2 border-l-transparent"
                }`}
              >
                <div className="flex flex-col items-center gap-1 w-16 shrink-0">
                  <Pill color={src.color}>{src.label}</Pill>
                  {isOpen && (
                    <div className={`text-[10px] font-bold flex items-center gap-0.5 ${tl.overdue ? "text-red-600" : "text-brand-text2"}`}>
                      <Clock size={9} /> {tl.label}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-brand-dark text-sm truncate">{t.subject}</div>
                  <div className="text-xs text-brand-text2 truncate mt-0.5">
                    {t.from_name ? `${t.from_name} · ` : ""}{t.from_email} · → {t.inbox}
                  </div>
                  {t.preview && <div className="text-xs text-slate-500 truncate mt-0.5">{t.preview}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Pill color={st.color}>{st.label}</Pill>
                  {t.priority !== "normal" && (
                    <span className="text-[9px] uppercase font-bold text-brand-text2">{PRIORITY_LABELS[t.priority]}</span>
                  )}
                </div>
                <ArrowUpRight size={14} className="text-slate-400 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <TicketDetailModal
          ticket={selected}
          mineOnly={mineOnly}
          onClose={() => setSelected(null)}
          onChange={(updated) => {
            setSelected(updated);
            setTickets((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
          }}
          onReloadList={load}
        />
      )}

      {showHelp && <ShortcutsHelp onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function ShortcutsHelp({ onClose }) {
  React.useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const SHORTCUTS = [
    { section: "Navigation i listen", items: [
      { keys: ["j"], desc: "Næste ticket i listen" },
      { keys: ["k"], desc: "Forrige ticket" },
      { keys: ["Enter"], desc: "Åbn fremhævet ticket" },
      { keys: ["r"], desc: "Genopfrisk liste" },
      { keys: ["/"], desc: "Fokus på filter" },
    ]},
    { section: "Filtre", items: [
      { keys: ["g", "a"], desc: "Vis alle tickets" },
      { keys: ["g", "m"], desc: "Vis kun mine tickets" },
    ]},
    { section: "I detail-modalen", items: [
      { keys: ["n"], desc: "Hop til næste åbne ticket" },
      { keys: ["c"], desc: "Luk ticket" },
      { keys: ["Esc"], desc: "Luk modal" },
    ]},
    { section: "Hjælp", items: [
      { keys: ["?"], desc: "Vis/skjul denne hjælp" },
    ]},
  ];

  const Kbd = ({ children }) => (
    <kbd className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 text-[11px] font-mono font-bold bg-slate-100 border border-slate-300 rounded-sm text-brand-dark shadow-sm">
      {children}
    </kbd>
  );

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="tickets-help-modal">
      <div className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-slate-200 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-brand-dark text-white px-5 py-4 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2">
            <Keyboard size={18} />
            <h3 className="font-display font-semibold text-base">Tastatur-genveje</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          {SHORTCUTS.map((sec) => (
            <div key={sec.section}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-red mb-2">{sec.section}</div>
              <div className="space-y-1.5">
                {sec.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-brand-dark">{it.desc}</span>
                    <div className="flex items-center gap-1">
                      {it.keys.map((kk, ki) => (
                        <React.Fragment key={ki}>
                          {ki > 0 && <span className="text-slate-400 text-[10px]">→</span>}
                          <Kbd>{kk}</Kbd>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-brand-text2 mt-4 pt-4 border-t border-slate-200">
            Tip: Genveje virker kun når du ikke skriver i et felt. Tryk <Kbd>?</Kbd> hvor som helst for at åbne denne dialog.
          </p>
        </div>
      </div>
    </div>
  );
}

function TicketDetailModal({ ticket, onClose, onChange, onReloadList, mineOnly }) {
  const [replyBody, setReplyBody] = React.useState("");
  const [replySubject, setReplySubject] = React.useState(`Re: ${ticket.subject}`);
  const [noteText, setNoteText] = React.useState("");
  const [closeAfterSend, setCloseAfterSend] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [drafting, setDrafting] = React.useState(false);
  const [related, setRelated] = React.useState(null);
  const [bookingDrawer, setBookingDrawer] = React.useState(null);
  const [bookingDrawerLoading, setBookingDrawerLoading] = React.useState(false);

  const src = SOURCE_LABELS[ticket.source] || SOURCE_LABELS.manual;

  const openBookingDrawer = async (bookingId) => {
    setBookingDrawer({ id: bookingId, data: null });
    setBookingDrawerLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${bookingId}/summary`);
      setBookingDrawer({ id: bookingId, data });
    } catch (e) {
      toast.error("Kunne ikke hente booking-detaljer");
      setBookingDrawer(null);
    } finally {
      setBookingDrawerLoading(false);
    }
  };

  // Load source-specific related entities (driver application, onboarding etc.)
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/tickets/${ticket.id}/related`);
        if (alive) setRelated(data);
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [ticket.id]);

  const sendOnboardingLink = async () => {
    const appId = related?.driver_application?.id;
    if (!appId) return;
    setBusy(true);
    try {
      await api.post(`/admin/driver-applications/${appId}/send-onboarding-link`);
      toast.success("Onboarding-link sendt til ansøger");
      // Reload related to update timestamp
      const { data } = await api.get(`/admin/tickets/${ticket.id}/related`);
      setRelated(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kunne ikke sende onboarding-link";
      toast.error(typeof msg === "string" ? msg : "Fejl");
    } finally {
      setBusy(false);
    }
  };

  const generateContracts = async () => {
    const onbId = related?.onboarding?.id;
    if (!onbId) {
      toast.error("Ansøgeren har ikke udfyldt onboarding endnu");
      return;
    }
    if (!window.confirm("Generér og email EMA-kontrakt + Burd samarbejdsaftale til ansøgeren?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/employment-onboardings/${onbId}/generate-contracts`);
      toast.success("Kontrakter genereret og sendt");
      const { data } = await api.get(`/admin/tickets/${ticket.id}/related`);
      setRelated(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kunne ikke generere kontrakter";
      toast.error(typeof msg === "string" ? msg : "Fejl");
    } finally {
      setBusy(false);
    }
  };

  const generateAIDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/ai-draft`);
      if (data.ok && data.draft) {
        setReplyBody(data.draft);
        if (data.subject) setReplySubject(data.subject);
        toast.success("AI-udkast genereret — gennemgå og send");
      } else {
        toast.error("Kunne ikke generere udkast");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI-tjenesten kunne ikke nås";
      toast.error(msg);
    } finally {
      setDrafting(false);
    }
  };

  const updateField = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/tickets/${ticket.id}`, patch);
      onChange(data);
      onReloadList();
      toast.success("Opdateret");
    } catch (e) {
      toast.error("Kunne ikke opdatere");
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${ticket.id}/notes`, { text: noteText });
      const { data } = await api.get(`/admin/tickets/${ticket.id}`);
      onChange(data);
      setNoteText("");
      toast.success("Note tilføjet");
    } catch (e) {
      toast.error("Kunne ikke tilføje note");
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!replyBody.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/reply`, {
        subject: replySubject,
        body: replyBody,
        close_after_send: closeAfterSend,
      });
      if (data.ok) {
        toast.success(closeAfterSend ? "Svar sendt + ticket lukket" : "Svar sendt");
        const { data: refreshed } = await api.get(`/admin/tickets/${ticket.id}`);
        onChange(refreshed);
        setReplyBody("");
        onReloadList();
      } else {
        toast.error("Email-systemet er ikke konfigureret — svaret er gemt internt");
      }
    } catch (e) {
      toast.error("Kunne ikke sende svar");
    } finally {
      setBusy(false);
    }
  };

  const jumpToNext = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ after_id: ticket.id });
      if (mineOnly) params.set("mine_only", "true");
      const { data } = await api.get(`/admin/tickets/next?${params.toString()}`);
      if (data.ticket) {
        onChange(data.ticket);
        // Reset transient state for the new ticket
        setReplyBody("");
        setReplySubject(`Re: ${data.ticket.subject}`);
        setNoteText("");
        setCloseAfterSend(false);
        toast.success("Næste ticket åbnet");
        onReloadList();
      } else {
        toast.success("🎉 Inbox-zero — ingen åbne tickets tilbage!");
        onClose();
      }
    } catch (e) {
      toast.error("Kunne ikke hente næste ticket");
    } finally {
      setBusy(false);
    }
  };

  // Keyboard shortcuts inside detail modal
  React.useEffect(() => {
    const isFormElement = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (isFormElement(document.activeElement)) return;
      if (e.key === "n") { e.preventDefault(); jumpToNext(); return; }
      if (e.key === "c" && ticket.status !== "closed") { e.preventDefault(); updateField({ status: "closed" }); return; }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id, ticket.status, mineOnly]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6" data-testid="ticket-detail-modal">
      <div className="absolute inset-0 bg-brand-dark/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-brand max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-brand-dark text-white px-6 py-5 flex items-start justify-between gap-3 border-b border-white/10 z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Pill color={src.color} bg="#fff">{src.label}</Pill>
              <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-white/60">→ {ticket.inbox}</span>
            </div>
            <h3 className="font-display font-semibold text-lg leading-tight truncate">{ticket.subject}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 transition-colors" data-testid="ticket-close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Inbox-zero workflow strip */}
        <div className="bg-purple-50 border-b border-purple-200 px-6 py-2.5 flex items-center justify-between gap-3" data-testid="ticket-workflow-strip">
          <div className="text-xs text-purple-900">
            {ticket.status === "closed" ? "✓ Ticket lukket" : "Arbejder på denne ticket"}{mineOnly && " · Mine"}
          </div>
          <button
            type="button"
            onClick={jumpToNext}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 transition-colors"
            data-testid="ticket-jump-next"
          >
            Hop til næste <ArrowUpRight size={12} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer history context (any source with from_email) */}
          {related?.customer_context && related.customer_context.total_bookings > 0 && (
            <div className="bg-sky-50 border border-sky-200 p-4" data-testid="ticket-customer-context">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sky-800 mb-3 flex items-center gap-1">
                <User size={11} /> Kunde-historik
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                <div>
                  <div className="text-xs text-brand-text2">Bookings i alt</div>
                  <div className="font-display font-black text-xl text-brand-dark">{related.customer_context.total_bookings}</div>
                </div>
                <div>
                  <div className="text-xs text-brand-text2">Samlet forbrug</div>
                  <div className="font-display font-black text-xl text-brand-dark">
                    {Math.round(related.customer_context.total_spent_dkk).toLocaleString("da-DK")} kr
                  </div>
                </div>
                <div>
                  <div className="text-xs text-brand-text2">Tier</div>
                  <div className="font-display font-black text-xl text-brand-dark">
                    {related.customer_context.total_bookings >= 20
                      ? "🏆 Trofast"
                      : related.customer_context.total_bookings >= 5
                      ? "⭐ Stamkunde"
                      : "Ny"}
                  </div>
                </div>
              </div>
              {related.customer_context.recent_bookings.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-800 mb-2">Seneste {related.customer_context.recent_bookings.length} bookings · klik for detaljer</div>
                  <div className="space-y-1.5">
                    {related.customer_context.recent_bookings.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => openBookingDrawer(b.id)}
                        className="w-full text-left text-xs flex items-center gap-2 bg-white p-2 border border-sky-100 hover:border-sky-400 hover:bg-sky-50 transition-colors"
                        data-testid={`ticket-recent-booking-${b.booking_number}`}
                      >
                        <span className="font-mono font-bold text-sky-900">{b.booking_number}</span>
                        <span className="text-brand-text2 truncate flex-1">
                          {b.pickup_city || "?"} → {b.delivery_city || "?"} · {b.service_type || ""}
                        </span>
                        <span className="text-brand-dark font-semibold whitespace-nowrap">
                          {b.total_dkk ? `${Math.round(b.total_dkk)} kr` : "—"}
                        </span>
                        <span className="text-[10px] uppercase font-bold text-brand-text2 whitespace-nowrap">{b.booking_status}</span>
                        <ArrowUpRight size={11} className="text-sky-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Source-specific quick-actions (driver application etc.) */}
          {ticket.source === "driver_application" && related && (
            <div className="bg-amber-50 border border-amber-200 p-4" data-testid="ticket-driver-actions">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800 mb-3 flex items-center gap-1">
                    <ArrowUpRight size={11} /> Chauffør-flow genveje
                  </div>
                  {/* Onboarding tracking pipeline */}
                  {related.onboarding_tracking && (
                    <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]" data-testid="onboarding-tracking-status">
                      {[
                        {
                          label: "Sendt",
                          ok: !!related.onboarding_tracking.sent_at,
                          at: related.onboarding_tracking.sent_at,
                        },
                        {
                          label: "Åbnet mail",
                          ok: !!related.onboarding_tracking.email_opened_at,
                          at: related.onboarding_tracking.email_opened_at,
                          note: related.onboarding_tracking.email_opened_at ? null : "Ikke sporbar / ikke åbnet",
                        },
                        {
                          label: "Klikket link",
                          ok: !!related.onboarding_tracking.link_clicked_at,
                          at: related.onboarding_tracking.link_clicked_at,
                        },
                        {
                          label: "Udfyldt",
                          ok: !!related.onboarding_tracking.completed,
                          at: related.onboarding_tracking.completed_at,
                        },
                      ].map((s) => (
                        <div key={s.label} className={`p-2 border ${s.ok ? "bg-emerald-50 border-emerald-200" : "bg-white border-amber-200"}`}>
                          <div className={`font-bold ${s.ok ? "text-emerald-800" : "text-amber-800"}`}>
                            {s.ok ? "✓" : "○"} {s.label}
                          </div>
                          <div className="text-brand-text2 mt-0.5">
                            {s.at
                              ? new Date(s.at).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                              : (s.note || "Afventer")}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-xs text-brand-text2">Onboarding-link</div>
                  <div className="font-semibold text-brand-dark">
                    {related.driver_application?.onboarding_link_sent_at || related.onboarding_tracking?.sent_at
                      ? `Sendt ${new Date(related.driver_application?.onboarding_link_sent_at || related.onboarding_tracking.sent_at).toLocaleDateString("da-DK", { day: "2-digit", month: "short" })}`
                      : "Endnu ikke sendt"}
                  </div>
                  <button
                    type="button"
                    onClick={sendOnboardingLink}
                    disabled={busy || !related.driver_application?.id}
                    className="mt-2 text-xs font-bold px-3 py-1.5 rounded-sm bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
                    data-testid="ticket-send-onboarding-link"
                  >
                    {(related.driver_application?.onboarding_link_sent_at || related.onboarding_tracking?.sent_at) ? "Send på ny" : "Send link"}
                  </button>
                </div>
                <div>
                  <div className="text-xs text-brand-text2">Ansættelseskontrakt</div>
                  <div className="font-semibold text-brand-dark">
                    {related.onboarding?.contracts_sent_at
                      ? `Sendt ${new Date(related.onboarding.contracts_sent_at).toLocaleDateString("da-DK", { day: "2-digit", month: "short" })}`
                      : related.onboarding
                      ? "Onboarding klar — generer kontrakt"
                      : "Venter på onboarding"}
                  </div>
                  <button
                    type="button"
                    onClick={generateContracts}
                    disabled={busy || !related.onboarding?.id || !!related.onboarding?.contracts_sent_at}
                    className="mt-2 text-xs font-bold px-3 py-1.5 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50"
                    data-testid="ticket-generate-contracts"
                  >
                    {related.onboarding?.contracts_sent_at ? "Allerede sendt" : "Generér kontrakter"}
                  </button>
                </div>
                  </div>
                </div>
            </div>
          )}

          {/* Sender info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <User size={14} className="text-brand-text2" />
              <strong className="text-brand-dark">{ticket.from_name || "—"}</strong>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-brand-text2" />
              <a href={`mailto:${ticket.from_email}`} className="text-brand-red font-semibold">{ticket.from_email}</a>
            </div>
            {ticket.from_phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-brand-text2" />
                <a href={`tel:${ticket.from_phone}`} className="text-brand-dark">{ticket.from_phone}</a>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Clock size={14} className="text-brand-text2" />
              <span className="text-brand-text2">SLA: {new Date(ticket.sla_due_at).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          </div>

          {/* Status + priority + assignee */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label-flat">Status</label>
              <select className="input-flat" value={ticket.status} disabled={busy} onChange={(e) => updateField({ status: e.target.value })} data-testid="ticket-status-select">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-flat">Prioritet</label>
              <select className="input-flat" value={ticket.priority} disabled={busy} onChange={(e) => updateField({ priority: e.target.value })} data-testid="ticket-priority-select">
                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="label-flat">Tildelt</label>
              <input
                type="email"
                className="input-flat"
                placeholder="admin@emakurer.dk"
                defaultValue={ticket.assigned_to || ""}
                disabled={busy}
                onBlur={(e) => { if (e.target.value !== (ticket.assigned_to || "")) updateField({ assigned_to: e.target.value }); }}
                data-testid="ticket-assignee-input"
              />
            </div>
          </div>

          {/* Original message preview */}
          {ticket.preview && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 mb-2">Oprindelig besked</div>
              <div className="bg-slate-50 border-l-2 border-brand-dark p-4 text-sm text-brand-dark whitespace-pre-wrap">{ticket.preview}</div>
            </div>
          )}

          {/* Reply form */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-red mb-2 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1"><Send size={11} /> Svar kunden</span>
              <button
                type="button"
                onClick={generateAIDraft}
                disabled={drafting || busy}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-bold px-2.5 py-1 rounded-sm border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-60 transition-colors"
                data-testid="ticket-ai-draft-btn"
              >
                {drafting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {drafting ? "Genererer..." : "AI-udkast"}
              </button>
            </div>
            <input
              className="input-flat mb-2"
              value={replySubject}
              onChange={(e) => setReplySubject(e.target.value)}
              data-testid="ticket-reply-subject"
            />
            <textarea
              className="input-flat min-h-[120px]"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder={`Hej ${ticket.from_name ? ticket.from_name.split(" ")[0] : "kunde"},\n\nTak for din henvendelse...`}
              data-testid="ticket-reply-body"
            />
            <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
              <label className="text-xs text-brand-text2 flex items-center gap-2">
                <input type="checkbox" checked={closeAfterSend} onChange={(e) => setCloseAfterSend(e.target.checked)} data-testid="ticket-close-after" />
                Luk ticket efter afsendelse
              </label>
              <button
                onClick={sendReply}
                disabled={busy || !replyBody.trim()}
                className="btn-primary"
                data-testid="ticket-reply-send"
              >
                <Send size={14} /> {busy ? "Sender..." : "Send svar"}
              </button>
            </div>
          </div>

          {/* Internal notes */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 mb-2 flex items-center gap-1">
              <MessageSquare size={11} /> Interne noter ({(ticket.notes || []).length})
            </div>
            {(ticket.notes || []).map((n, i) => (
              <div key={i} className="bg-amber-50 border-l-2 border-amber-400 p-3 text-xs text-brand-dark mb-2">
                <div className="font-semibold">{n.by}</div>
                <div className="opacity-70 text-[10px]">{new Date(n.at).toLocaleString("da-DK")}</div>
                <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <input
                className="input-flat flex-1"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Tilføj intern note..."
                data-testid="ticket-note-input"
              />
              <button onClick={addNote} disabled={busy || !noteText.trim()} className="btn-secondary" data-testid="ticket-note-add">+</button>
            </div>
          </div>

          {/* Outgoing replies history */}
          {(ticket.replies || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 mb-2">Sendt fra os ({ticket.replies.length})</div>
              {ticket.replies.map((r, i) => (
                <div key={i} className="bg-emerald-50 border-l-2 border-emerald-500 p-3 text-xs text-brand-dark mb-2">
                  <div className="font-semibold flex items-center gap-2">
                    {r.by} → {r.to_email}
                    {r.ok ? <Pill color="#10B981">Sendt</Pill> : <Pill color="#DC2626">Email-fejl</Pill>}
                  </div>
                  <div className="opacity-70 text-[10px]">{new Date(r.at).toLocaleString("da-DK")} · {r.subject}</div>
                  <div className="mt-1 whitespace-pre-wrap">{r.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Inline booking drawer */}
        {bookingDrawer && (
          <BookingDrawer
            id={bookingDrawer.id}
            data={bookingDrawer.data}
            loading={bookingDrawerLoading}
            onClose={() => setBookingDrawer(null)}
          />
        )}
      </div>
    </div>
  );
}

function BookingDrawer({ id, data, loading, onClose }) {
  React.useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const StatusPill = ({ children, bg = "#0A1128", color = "#fff" }) => (
    <span className="inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5"
      style={{ background: bg, color }}>{children}</span>
  );

  return (
    <div className="absolute inset-0 bg-white z-20 flex flex-col" data-testid="booking-drawer">
      <div className="sticky top-0 bg-sky-700 text-white px-5 py-3 flex items-center justify-between border-b border-sky-900">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 -ml-1" aria-label="Tilbage" data-testid="booking-drawer-back">
            <X size={16} />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 font-bold">Booking-detaljer</div>
            <div className="font-display font-bold text-base truncate">{data?.booking_number || "Indlæser..."}</div>
          </div>
        </div>
        {data?.booking_status && <StatusPill bg="#fff" color="#0369A1">{data.booking_status}</StatusPill>}
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {loading || !data ? (
          <div className="text-brand-text2 py-12 text-center">Indlæser booking-detaljer...</div>
        ) : (
          <>
            {/* Top stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2">Pris</div>
                <div className="font-display font-black text-lg text-brand-dark">{data.total_dkk ? `${Math.round(data.total_dkk)} kr` : "—"}</div>
              </div>
              <div className="bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2">Afstand</div>
                <div className="font-display font-black text-lg text-brand-dark">{data.distance_km ? `${data.distance_km.toFixed(1)} km` : "—"}</div>
              </div>
              <div className="bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2">Betaling</div>
                <div className="font-display font-black text-lg text-brand-dark">{data.payment_status || "—"}</div>
              </div>
              <div className="bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2">Service</div>
                <div className="font-display font-black text-base text-brand-dark truncate">{data.service_name || data.service_type || "—"}</div>
              </div>
            </div>

            {/* Pickup → Delivery */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="border-l-2 border-brand-red pl-4">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-red mb-1">Afhentning</div>
                <div className="text-sm font-semibold text-brand-dark">{data.pickup_address || "—"}</div>
                <div className="text-xs text-brand-text2">{data.pickup_postcode} {data.pickup_city}</div>
                {data.scheduled_pickup_at && (
                  <div className="text-xs text-brand-text2 mt-1">
                    🕒 {new Date(data.scheduled_pickup_at).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
              <div className="border-l-2 border-emerald-600 pl-4">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1">Levering</div>
                <div className="text-sm font-semibold text-brand-dark">{data.delivery_address || "—"}</div>
                <div className="text-xs text-brand-text2">{data.delivery_postcode} {data.delivery_city}</div>
                {data.scheduled_delivery_at && (
                  <div className="text-xs text-brand-text2 mt-1">
                    🕒 {new Date(data.scheduled_delivery_at).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
                {data.delivered_at && (
                  <div className="text-xs text-emerald-700 font-semibold mt-1">
                    ✓ Leveret {new Date(data.delivered_at).toLocaleString("da-DK", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </div>

            {/* Kunde + Chauffør */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 mb-1">Kunde</div>
                <div className="text-sm font-semibold text-brand-dark">{data.customer_name || "—"}</div>
                <div className="text-xs text-brand-text2">{data.customer_email}</div>
                {data.customer_phone && <div className="text-xs text-brand-text2">{data.customer_phone}</div>}
              </div>
              <div className="bg-slate-50 p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 mb-1">Chauffør</div>
                <div className="text-sm font-semibold text-brand-dark">{data.driver_name || "Ikke tildelt"}</div>
                {data.driver_phone && <div className="text-xs text-brand-text2">{data.driver_phone}</div>}
              </div>
            </div>

            {/* Notes */}
            {data.notes && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brand-text2 mb-2">Noter</div>
                <div className="bg-amber-50 border-l-2 border-amber-400 p-3 text-sm text-brand-dark whitespace-pre-wrap">{data.notes}</div>
              </div>
            )}

            {/* Proof photos */}
            {data.proof_photos && data.proof_photos.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-2 flex items-center gap-1">
                  ✓ Foto-bevis ({data.proof_count})
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {data.proof_photos.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden border border-slate-200 hover:border-emerald-500 transition-colors">
                      <img src={url} alt={`Bevis ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Pakkeshop info */}
            {data.pakkeshop_name && (
              <div className="bg-purple-50 border border-purple-200 p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-purple-800 mb-1">Pakkeshop</div>
                <div className="text-sm font-semibold text-brand-dark">{data.pakkeshop_name}</div>
                {data.pickup_code && <div className="text-xs text-brand-text2 mt-1">Afhentnings-kode: <span className="font-mono font-bold">{data.pickup_code}</span></div>}
              </div>
            )}

            {/* Quick links */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
              {data.invoice_number && (
                <a href={`/admin/invoices/${data.invoice_number}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-sky-700 underline">
                  Faktura {data.invoice_number} →
                </a>
              )}
              <a href={`/admin#bookings`} className="text-xs font-semibold text-brand-red underline">
                Åbn i Bookings-tab →
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
